import {Divider, Stack} from '@mui/material';
import {format, parseISO} from 'date-fns';
import {Fragment, startTransition, useCallback, useEffect, useMemo} from 'react';
import {useForm} from 'react-hook-form';
import {DATE_FORMAT_FNS} from '../../../../shared/constants/date-formats';
import {useModalPageStore} from '../../../../store/modalPage';
import {useProfileData} from '../../hooks/use-profile-data.hook';
import {ProfileFormValues} from '../../types/profile.types';
import {ContactSection} from '../sections/contact-section';
import {DiskUsageSection} from '../sections/disk-usage-section';
import {RequisitesSection} from '../sections/requisites-section';
import {TariffSection} from '../sections/tariff-section';
import {Spinner} from '../spinner';

const formatDate = (value?: string) => {
  if (!value) return '-';
  try {
    return format(parseISO(value), DATE_FORMAT_FNS);
  } catch {
    return '-';
  }
};

export const ProfileForm = () => {
  const {loading, directorsLoading, directors, formData, updateCompany, company} = useProfileData();
  const {setModalKey} = useModalPageStore();

  const {
    control,
    handleSubmit,
    register,
    reset,
    formState: {errors, isDirty},
  } = useForm<ProfileFormValues>({
    defaultValues: formData,
  });

  useEffect(() => {
    reset(formData);
  }, [formData, reset]);

  const onSubmit = useCallback(
    async (values: ProfileFormValues) => {
      const isSaved = await updateCompany(values);
      if (isSaved) {
        reset(values);
      }
    },
    [reset, updateCompany],
  );

  const usedDiskSpace = company?.tariff?.usedDiskSpace ?? 0;
  const totalDiskSpace = company?.tariff?.diskSpace ?? 0;
  const usedDiskSpacePercent = useMemo(() => {
    if (!totalDiskSpace) return 0;
    return Math.min(100, Math.round((usedDiskSpace / totalDiskSpace) * 100));
  }, [totalDiskSpace, usedDiskSpace]);

  const tariffTitle = company?.tariff?.title ? `"${company.tariff.title.toUpperCase()}"` : '-';
  const tariffEndDate = formatDate(company?.tariff?.tariffEndDate);

  const handleOpenBalance = () => {
    startTransition(() => setModalKey('balancePayment'));
  };

  const handleOpenTariffs = () => {
    startTransition(() => setModalKey('clientTariffs'));
  };

  return (
    <Fragment>
      <Spinner loading={loading} />
      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack gap="24px">
          <RequisitesSection
            control={control}
            register={register}
            directors={directors}
            directorsLoading={directorsLoading}
          />
          <Divider />
          <ContactSection control={control} register={register} errors={errors} canSave={isDirty} loading={loading} />
          <Divider />
          <TariffSection
            tariffTitle={tariffTitle}
            tariffEndDate={tariffEndDate}
            onOpenTariffs={handleOpenTariffs}
            onOpenBalance={handleOpenBalance}
          />
          <Divider />
          <DiskUsageSection percent={usedDiskSpacePercent} />
        </Stack>
      </form>
    </Fragment>
  );
};
